import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Star,
  TrendingUp,
  Shield,
  Globe,
  Users,
  Award,
  CheckCircle,
  ArrowRight,
  Zap,
  Headphones,
  Sparkles,
  BarChart3,
  DollarSign,
} from "lucide-react";

export default function Home() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({
    name: "",
    rating: "",
    comment: "",
    image: null,
  });
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch all reviews
  const fetchReviews = async () => {
    try {
      const res = await axios.get("https://api.kestren.com/api/reviews");
      setReviews(res.data.reviews || []);
    } catch (err) {
      console.error("Error fetching reviews:", err);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Submit review
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.rating || !form.comment) {
      alert("Please fill all fields");
      return;
    }

    const formData = new FormData();
    formData.append("name", form.name);
    formData.append("rating", form.rating);
    formData.append("comment", form.comment);
    if (form.image) formData.append("image", form.image);

    try {
      setLoading(true);
      await axios.post("https://api.kestren.com/api/reviews/add", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({ name: "", rating: "", comment: "", image: null });
      setIsModalOpen(false);
      fetchReviews();
      alert("Review submitted successfully!");
    } catch (err) {
      console.error(err);
      alert("Error submitting review");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* ================= HERO SECTION - UPDATED CONTENT ================= */}
      <section className="relative bg-gradient-to-br from-slate-900 via-black to-slate-900 pt-32 pb-24 overflow-hidden border-b border-white/10">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-grid-white/5"></div>
        <div className="absolute top-20 left-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute bottom-20 right-10 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>

        <div className="relative max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in">
            <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold">
              Partner with Professional Traders & Share the Profits
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight">
              Invest Smart.
              <br />
              Grow Consistently.
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
                With KESTREN
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              You invest. Our professional trading team manages your capital
              with risk-controlled strategies and we share the profits with you
              in a transparent partnership model.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold rounded-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 group shadow-lg shadow-cyan-500/50"
              >
                Explore Services
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition" />
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 bg-white/5 hover:bg-white/10 text-white font-bold rounded-xl transition-all border border-white/20 hover:border-cyan-500/50 flex items-center justify-center gap-2"
              >
                Start Investing
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-6 pt-4">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Transparent profit sharing
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Risk-managed strategies
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Personal support & updates
              </div>
            </div>

            <p className="text-xs text-gray-500 pt-2">
              *Trading in financial markets involves risk. Capital is subject to
              market movements and returns are not guaranteed.
            </p>
          </div>

          {/* Right Content - Feature Cards */}
          <div
            className="relative animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            <div className="absolute -inset-4 bg-gradient-to-tr from-cyan-500/20 via-blue-500/10 to-transparent blur-3xl rounded-3xl"></div>
            <div className="relative rounded-2xl border border-white/20 p-8 bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm">
              <div className="grid grid-cols-2 gap-4">
                {[
                  {
                    icon: Zap,
                    title: "Pro Trading Team",
                    desc: "Full-time traders managing your capital.",
                    color: "from-yellow-400 to-orange-500",
                  },
                  {
                    icon: TrendingUp,
                    title: "Profit Sharing",
                    desc: "We earn only when you profit.",
                    color: "from-cyan-400 to-blue-500",
                  },
                  {
                    icon: Shield,
                    title: "Risk Management",
                    desc: "Strict rules to protect capital.",
                    color: "from-green-400 to-emerald-500",
                  },
                  {
                    icon: Headphones,
                    title: "Regular Updates",
                    desc: "Reports & communication you can trust.",
                    color: "from-purple-400 to-pink-500",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="group relative rounded-xl bg-black/40 border border-white/10 p-5 hover:border-cyan-500/50 transition-all hover:-translate-y-1"
                  >
                    <div
                      className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.color}  flex items-center justify-center mb-3 group-hover:opacity-40 transition`}
                    >
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <p className="font-bold text-lg mb-1">{item.title}</p>
                    <p className="text-sm text-gray-400">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= STATS COUNTER - UPDATED CONTENT ================= */}
      <section className="py-20 bg-gradient-to-r from-cyan-600/10 via-blue-600/10 to-purple-600/10 border-y border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/5"></div>
        <div className="relative max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { number: "300+", label: "Active Partners", icon: Users },
            { number: "$5M+", label: "Capital Managed", icon: DollarSign },
            { number: "3–12 mo", label: "Typical Horizon", icon: Globe },
            { number: "92%", label: "Client Retention", icon: Award },
          ].map((stat, i) => (
            <div
              key={i}
              className="group space-y-3 p-6 rounded-xl hover:bg-white/5 transition-all"
            >
              <stat.icon className="w-8 h-8 mx-auto text-cyan-400 group-hover:scale-110 transition-transform" />
              <h3 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                {stat.number}
              </h3>
              <p className="text-gray-400 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================= FEATURES SECTION - UPDATED CONTENT ================= */}
      <section className="py-24 relative overflow-hidden bg-gradient-to-b from-black via-slate-900/50 to-black">
        <div className="absolute inset-0 bg-grid-white/5"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold">
              ⚡ Professional Trading Partnership
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
              Built for{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Investors Who Want Experts to Trade
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              You don&apos;t need to sit in front of charts. We do the trading —
              you share the profits.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: BarChart3,
                title: "Professional Strategies",
                desc: "Tested trading systems built on technical & fundamental analysis.",
                color: "from-cyan-400 to-blue-500",
              },
              {
                icon: Zap,
                title: "Hands-Free for You",
                desc: "We handle entries, exits, and risk so you can focus on your life or business.",
                color: "from-yellow-400 to-orange-500",
              },
              {
                icon: Shield,
                title: "Risk-First Approach",
                desc: "Position sizing, stop-loss rules, and capital protection at the core.",
                color: "from-green-400 to-emerald-500",
              },
              {
                icon: Globe,
                title: "Multiple Markets",
                desc: "We trade across different instruments and conditions to diversify exposure.",
                color: "from-purple-400 to-pink-500",
              },
              {
                icon: Headphones,
                title: "Clear Communication",
                desc: "Regular updates, performance summaries, and a real person to talk to.",
                color: "from-blue-400 to-indigo-500",
              },
              {
                icon: Award,
                title: "Aligned Incentives",
                desc: "We grow only when you grow. No profits means we don&apos;t get paid.",
                color: "from-rose-400 to-red-500",
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="group relative bg-gradient-to-br from-white/5 to-white/10 border border-white/20 rounded-2xl p-8 backdrop-blur-sm transition-all duration-500 hover:-translate-y-3 hover:border-cyan-500/50 overflow-hidden"
              >
                {/* Hover glow effect */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                ></div>

                <div className="relative">
                  <div
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color}  flex items-center justify-center mb-6 group-hover:opacity-40 group-hover:scale-110 transition-all`}
                  >
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 group-hover:text-cyan-400 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= REVIEW SECTION - TEXT TWEAK ================= */}
  

      {/* ================= FAQ SECTION - UPDATED CONTENT ================= */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
              Frequently Asked{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Questions
              </span>
            </h2>
            <p className="text-xl text-gray-400">
              Everything you need to know about partnering with our traders
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "How does the profit-sharing model work?",
                a: "You invest a decided amount, our team trades on your behalf, and profits are shared between you and us as per a predefined percentage. If there is no profit, we don’t take a performance fee.",
              },
              {
                q: "What is the minimum investment required?",
                a: "We typically start from a small, comfortable amount so you can test the process first. The exact minimum can be discussed based on your risk appetite and goals.",
              },
              {
                q: "Do I need any trading knowledge?",
                a: "No. You don’t need to know charts, indicators, or strategies. Our team handles all trading decisions — you only need to understand the risk and agree to the terms.",
              },
              {
                q: "Can I withdraw my capital?",
                a: "Yes, you can request withdrawals as per the agreed notice period. We believe in transparency and want you to feel fully in control of your money.",
              },
              {
                q: "Is my money safe?",
                a: "Every investment in markets carries risk. We focus on capital protection and risk management, but we never promise guaranteed returns. We’ll clearly explain the risk before you start.",
              },
            ].map((faq, i) => (
              <details
                key={i}
                className="group bg-gradient-to-br from-white/5 to-white/10 border border-white/20 rounded-xl overflow-hidden hover:border-cyan-500/50 transition-all"
              >
                <summary className="px-6 py-5 font-semibold cursor-pointer flex justify-between items-center hover:text-cyan-400 transition text-lg">
                  {faq.q}
                  <span className="text-2xl text-cyan-400 group-open:rotate-180 transition-transform">
                    ↓
                  </span>
                </summary>
                <p className="px-6 pb-6 text-gray-400 leading-relaxed border-t border-white/10">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA - UPDATED CONTENT ================= */}
      <section className="relative py-24 bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer"></div>

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6">
            Ready to Grow Your Money with Professionals?
          </h2>
          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Let our trading team do the work. You stay in control of your
            capital and share the profits we generate together.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link
              to="/contact"
              className="px-10 py-5 bg-white text-blue-600 font-bold rounded-xl hover:bg-gray-100 transition-all text-lg transform hover:scale-105 shadow-xl"
            >
              Talk to Us About Investment
            </Link>
            <Link
              to="/services"
              className="px-10 py-5 border-2 border-white text-white font-bold rounded-xl hover:bg-white/10 transition-all text-lg transform hover:scale-105"
            >
              View Partnership Services
            </Link>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-white/90">
            <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
              <CheckCircle className="w-5 h-5" /> No profit, no performance fee
            </span>
            <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
              <CheckCircle className="w-5 h-5" /> Clear terms & reporting
            </span>
            <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
              <CheckCircle className="w-5 h-5" /> Human-to-human support
            </span>
          </div>
        </div>
      </section>

      {/* ================= MODAL - TEXT TWEAK ================= */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-lg flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-gradient-to-br from-slate-900 to-black border border-white/20 rounded-2xl p-10 w-full max-w-md shadow-2xl shadow-cyan-500/20 relative animate-scale-in">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-white/60 hover:text-white text-2xl hover:bg-white/10 rounded-full w-10 h-10 flex items-center justify-center transition hover:rotate-90"
            >
              ✕
            </button>

            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Share Your Experience
              </h3>
              <p className="text-gray-400 mt-2">
                Tell us about your journey investing with KESTREN
              </p>
            </div>

            <div className="space-y-5">
              <input
                type="text"
                placeholder="Your Full Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full p-4 rounded-xl bg-white/5 border border-white/20 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 transition"
              />
              <div className="relative">
                <select
                  value={form.rating}
                  onChange={(e) => setForm({ ...form, rating: e.target.value })}
                  className="w-full p-4 rounded-xl bg-white/5 border border-white/20 text-white focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 transition appearance-none"
                >
                  <option value="">Select Rating</option>
                  <option value="5">⭐⭐⭐⭐⭐ Excellent</option>
                  <option value="4">⭐⭐⭐⭐ Very Good</option>
                  <option value="3">⭐⭐⭐ Good</option>
                  <option value="2">⭐⭐ Fair</option>
                  <option value="1">⭐ Poor</option>
                </select>
              </div>
              <textarea
                placeholder="Share your experience working with our trading team..."
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                className="w-full p-4 rounded-xl bg-white/5 border border-white/20 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 h-32 resize-none transition"
              ></textarea>
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Add Photo (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setForm({ ...form, image: e.target.files[0] })
                  }
                  className="w-full text-white/80 file:mr-4 file:py-3 file:px-6 file:rounded-lg file:border-0 file:bg-cyan-500 file:text-white hover:file:bg-cyan-600 cursor-pointer file:transition"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full py-5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold text-lg shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Review
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-scale-in {
          animation: scale-in 0.4s ease-out forwards;
        }

        .animate-shimmer {
          animation: shimmer 3s infinite;
        }

        .bg-grid-white\\/5 {
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 50px 50px;
        }

        .bg-grid-white\\/10 {
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </div>
  );
}
